// Contact groups: hand-picked members, stored as an eshot_audiences doc with
// kind 'list' and contactIds (eshot_contacts ids). CSV imports write the same
// shape, so the dispatcher and the composer already resolve both.
// Spec: docs/superpowers/specs/2026-10-26-eshot-contact-groups-design.md.

// About 200 KB of ids, well inside Firestore's 1 MiB document limit.
export const GROUP_MAX = 5000;

export function isGroup(audience) {
  return audience?.kind === 'list';
}

/**
 * 'group' (hand-picked), 'smart-group' (kept up to date from its rule),
 * 'imported' (a CSV list) or 'smart' (an old filter audience).
 */
export function audienceKind(audience) {
  if (!isGroup(audience)) return 'smart';
  if (isSmartGroup(audience)) return 'smart-group';
  return audience.importMeta ? 'imported' : 'group';
}

// A group saved from the builder that keeps its rule and is refreshed from it
// (functions/lib/eshot-smart-groups.js isSmart).
export function isSmartGroup(audience) {
  return isGroup(audience) && audience.smart === true && !!audience.recipe?.filters;
}

export const KIND_LABEL = { group: 'Group', 'smart-group': 'Smart group', imported: 'Imported list', smart: 'Smart audience' };

export function memberIds(audience) {
  return Array.isArray(audience?.contactIds)
    ? audience.contactIds.filter((id) => typeof id === 'string' && id)
    : [];
}

/**
 * The member list after adding `ids`: existing order kept, new ids appended,
 * no duplicates. Throws when the result would pass GROUP_MAX, so a group is
 * never written half-full.
 */
export function withMembersAdded(current, ids) {
  const out = [...new Set(memberIds({ contactIds: current }))];
  const have = new Set(out);
  for (const id of ids || []) {
    if (typeof id !== 'string' || !id || have.has(id)) continue;
    have.add(id);
    out.push(id);
  }
  if (out.length > GROUP_MAX) {
    throw new Error(`A group can hold ${GROUP_MAX.toLocaleString('en-GB')} people; this would make it ${out.length.toLocaleString('en-GB')}.`);
  }
  return out;
}

export function withMembersRemoved(current, ids) {
  const drop = new Set(ids || []);
  return [...new Set(memberIds({ contactIds: current }))].filter((id) => !drop.has(id));
}

export function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}
